import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { LogoutComponent } from './shared/logout/logout.component';
import { TranslationResolver } from './services/translation-resolver.service';
import { AlertGuard } from './core/guards/alert.guard';
import { RoleGuard } from './core/guards/role.guard';
import { HomeRedirectGuard } from './core/guards/home-redirect.guard';
import { UserInactiveGuard } from './core/guards/user-inactive.guard';
import { BudgetDirtyGuard } from './core/guards/buget-dirty.guard';
import { FeatureFlagGuard } from './core/guards/feature-flag.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [MsalGuard, HomeRedirectGuard],
    children: []
  },
  {
    path: 'home',
    loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
    data: {
      titleKey: 'homeTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'home-reports',
    loadChildren: () => import('./pages/home-reports/home-reports.module').then((m) => m.HomeReportsModule),
    data: {
      titleKey: 'homeReportsTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'suppliers',
    loadChildren: () => import('./pages/suppliers/suppliers.module').then((m) => m.SuppliersModule),
    data: {
      titleKey: 'testTitle',
      icon: '../assets/icons/active-routes-adress-line.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, AlertGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'prices',
    loadChildren: () => import('./pages/prices/prices.module').then((m) => m.PricesModule),
    data: {
      titleKey: 'pricesTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, AlertGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'marketplace',
    loadChildren: () => import('./pages/marketplace/marketplace.module').then((m) => m.MarketplaceModule),
    data: {
      titleKey: 'marketplaceTitle',
      icon: '../assets/icons/cafler.svg',
      // allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, AlertGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'group',
    loadChildren: () => import('./pages/group/group.module').then((m) => m.GroupModule),
    data: {
      titleKey: 'groupTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, AlertGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'widget',
    loadChildren: () => import('./pages/widget/widget.module').then((m) => m.WidgetModule),
    data: {
      titleKey: 'widgetTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, AlertGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'login',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'settings',
    loadChildren: () => import('./pages/settings/settings.module').then((m) => m.SettingsModule),
    data: {
      titleKey: 'settingsTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'center-management',
    loadChildren: () => import('./pages/center-management/center-management.module').then((m) => m.CenterManagementModule),
    data: {
      titleKey: 'centerManagementTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['superadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'calendar',
    loadChildren: () => import('./pages/calendar/calendar.module').then((m) => m.CalendarModule),
    data: {
      titleKey: 'calendarTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'calendar/:id',
    loadChildren: () => import('./pages/work-orders-protocols/service-detail/service-detail.module').then((m) => m.ServiceDetailModule),
    data: {
      titleKey: 'serviceDetailTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'kanban',
    loadChildren: () => import('./pages/work-orders-protocols/kanban/kanban.module').then((m) => m.ProtocolKanbanModule),
    data: {
      titleKey: 'kanban.title',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'services',
    loadChildren: () =>
      import('./pages/services/services.module').then((m) => m.ServicesModule),
    data: {
      titleKey: 'servicesTitle',
      icon: '../assets/icons/backoffice/servicios.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'password-recovery',
    loadChildren: () =>
      import('./pages/password-recovery/password-recovery.module').then(
        (m) => m.PasswordRecoveryModule
      ),
    data: { titleKey: 'passwordRecoveryTitle', icon: '../assets/icons/cafler.svg' },
    canActivate: [MsalGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'chat-ai',
    loadChildren: () => import('./pages/chat-ai/chat-ai.module').then((m) => m.ChatAiModule),
    data: {
      titleKey: 'chatAi.title',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser', 'superadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'chat',
    loadChildren: () => import('./pages/chat/chat.module').then((m) => m.ChatModule),
    data: {
      titleKey: 'chatTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'my-account',
    loadChildren: () =>
      import('./pages/my-account/my-account.module').then((m) => m.MyAccountModule),
    data: { titleKey: 'myAccountTitle', icon: '../assets/icons/cafler.svg', allowedRoles: ['centeradmin', 'groupadmin', 'centeruser'] },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'my-workshop',
    loadChildren: () =>
      import('./pages/my-workshop/my-workshop.module').then((m) => m.MyWorkshopModule),
    data: {
      titleKey: 'myWorkshopTitle',
      icon: '../assets/icons/navbar/nav-settings.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, AlertGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'new-service',
    loadChildren: () =>
      import('./pages/new-service/new-service.module').then((m) => m.NewServiceModule),
    data: {
      titleKey: 'newServiceTitle',
      icon: '../assets/icons/navbar/nav-new-service.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'employees',
    loadChildren: () => import('./pages/employees/employees.module').then((m) => m.EmployeesModule),
    data: {
      titleKey: 'users_title',
      icon: '../assets/icons/navbar/nav-users.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'employees/:id',
    loadChildren: () => import('./pages/employees/employees.module').then((m) => m.EmployeesModule),
    data: {
      titleKey: 'users_title',
      icon: '../assets/icons/navbar/nav-users.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver }
  },
  {
    path: 'orders',
    loadChildren: () => import('./pages/orders/orders.module').then((m) => m.OrdersModule),
    data: {
      titleKey: 'users_title',
      icon: '../assets/icons/navbar/nav-users.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'reports',
    loadChildren: () => import('./pages/reports/reports.module').then((m) => m.ReportsModule),
    data: {
      titleKey: 'reportsTitle',
      icon: '../assets/icons/navbar/nav-chart-bar.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
  {
    path: 'fleets',
    loadChildren: () => import('./pages/fleets/fleets.module').then((m) => m.FleetsModule),
    data: {
      titleKey: 'fleetsTitle',
      icon: '../assets/icons/navbar/nav-fleets.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'clients',
    loadChildren: () => import('./pages/clients/clients.module').then(m => m.ClientsModule),
    data: {
      titleKey: 'clientsTitle',
      icon: '../assets/icons/backoffice/clients.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'vehicles',
    loadChildren: () => import('./pages/vehicles/vehicles.module').then(m => m.VehiclesModule),
    data: {
      titleKey: 'vehiclesTitle',
      icon: '../assets/icons/backoffice/car-side-icon.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'test-tap-to-pay',
    loadChildren: () => import('./pages/test-tap-to-pay/test-tap-to-pay.module').then(m => m.TestTapToPayModule),
    data: {
      titleKey: 'testTapToPayTitle',
      icon: '../assets/icons/backoffice/car-side-icon.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: {
      title: TranslationResolver
    }
  },
  {
    path: 'orders-panel',
    loadChildren: () => import('./pages/orders-panel/orders-panel.module').then(m => m.OrdersPanelModule),
    data: {
      titleKey: 'ordersPanelTitle',
      icon: '../assets/icons/backoffice/orders-panel.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'storage',
    loadChildren: () => import('./pages/storage/storage.module').then(m => m.StorageModule),
    data: {
      titleKey: 'storageTitle',
      icon: '../assets/icons/backoffice/car-side-icon.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'budget',
    loadChildren: () => import('./pages/budget/budget.module').then(m => m.BudgetModule),
    data: {
      titleKey: 'budgetTitle',
      icon: '../assets/icons/backoffice/budget.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'budget-list',
    loadChildren: () => import('./pages/budget-list/budget-list.module').then(m => m.BudgetListModule),
    data: {
      titleKey: 'budgetListTitle',
      icon: '../assets/icons/backoffice/budget.svg',
      allowedRoles: ['centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'notifications',
    loadChildren: () => import('./pages/notifications/notifications.module').then(m => m.NotificationsModule),
    data: {
      titleKey: 'notificationsTitle',
      icon: '../assets/icons/notification.svg',
      allowedRoles: ['superadmin', 'centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'notification-settings',
    loadChildren: () => import('./pages/notification-settings/notification-settings.module').then(m => m.NotificationSettingsModule),
    data: {
      titleKey: 'notificationSettingsTitle',
      icon: '../assets/icons/navbar/nav-notifications.svg',
      allowedRoles: ['groupadmin', 'centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'user-inactive',
    loadChildren: () => import('./pages/user-inactive/user-inactive.module').then(m => m.UserInactiveModule),
    canActivate: [UserInactiveGuard]
  },
  {
    path: 'admin-content-management',
    loadChildren: () => import('./pages/admin-content-management/admin-content-management.module').then(m => m.AdminContentManagementModule),
    data: {
      titleKey: 'adminContentManagementTitle',
      icon: '../assets/icons/admin_panel_settings.svg',
      allowedRoles: ['groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'stock-movements',
    loadChildren: () => import('./pages/stock-movements/stock-movements.module').then(m => m.StockMovementsModule),
    data: {
      titleKey: 'stockMovementsTitle',
      icon: '../assets/icons/backoffice/stock.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'stock',
    loadChildren: () => import('./pages/stock/stock.module').then(m => m.StockListModule),
    data: {
      titleKey: 'stockTitle',
      icon: '../assets/icons/backoffice/stock.svg',
      allowedRoles: ['centeradmin', 'groupadmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'work-orders',
    loadChildren: () => import('./pages/work-orders-protocols/work-orders.module').then((m) => m.WorkOrdersModule),
    data: {
      titleKey: 'workOrdersTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver }
  },
  {
    path: 'my-tasks',
    loadChildren: () => import('./pages/my-tasks/my-tasks.module').then((m) => m.MyTasksModule),
    data: {
      titleKey: 'myTasksTitle',
      icon: '../assets/icons/task.svg',
      allowedRoles: ['centeradmin', 'groupadmin', 'centeruser']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver }
  },
  {
    path: 'wallet',
    loadChildren: () => import('./pages/wallet/wallet.module').then((m) => m.WalletModule),
    data: {
      titleKey: 'walletTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin'],
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'fintech',
    loadChildren: () => import('./pages/fintech/fintech.module').then((m) => m.FintechModule),
    data: {
      titleKey: 'fintechTitle',
      icon: '../assets/icons/cafler.svg',
      allowedRoles: ['centeradmin', 'groupadmin'],
      featureFlag: 'fintech'
    },
    canActivate: [MsalGuard, FeatureFlagGuard, RoleGuard],
    resolve: { title: TranslationResolver },
  },
  {
    path: 'notification-center',
    loadChildren: () => import('./pages/notification-center/notification-center.module').then((m) => m.NotificationCenterModule),
    data: {
      titleKey: 'notificationCenterTitle',
      icon: '../assets/icons/notification.svg',
      allowedRoles: ['groupadmin', 'centeradmin']
    },
    canActivate: [MsalGuard, RoleGuard],
    resolve: { title: TranslationResolver }
  },
  {
    path: 'cta/confirm',
    loadChildren: () => import('./pages/cta-confirm/cta-confirm.module').then((m) => m.CtaConfirmModule),
    data: { hideChrome: true }
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
